import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import type { PaymentCallbackStatus } from "./payment-copy";

export function usePaymentCallback() {
  const [searchParams] = useSearchParams();
  const reference =
    searchParams.get("reference")?.trim() || searchParams.get("trxref")?.trim() || null;

  const [status, setStatus] = useState<PaymentCallbackStatus>("verifying");
  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [invoiceType, setInvoiceType] = useState<string | null>(null);
  const [portalBaseUrl, setPortalBaseUrl] = useState<string | null>(null);
  const [digitalResourceId, setDigitalResourceId] = useState<string | null>(null);

  useEffect(() => {
    if (!reference) {
      setStatus("failed");
      return;
    }

    const verify = async () => {
      try {
        const response = await api.get<{
          success: boolean;
          invoiceId?: string;
          invoiceType?: string;
          portalBaseUrl?: string;
          digitalResourceId?: string;
        }>("payment-callback/complete", { params: { reference } });
        setPortalBaseUrl(response.data?.portalBaseUrl?.replace(/\/$/, "") ?? null);
        if (response.data?.success) {
          setInvoiceId(response.data.invoiceId ?? null);
          setInvoiceType(response.data.invoiceType ?? null);
          setDigitalResourceId(response.data.digitalResourceId ?? null);
          setStatus("success");
          return;
        }
        setStatus("failed");
      } catch {
        setStatus("failed");
      }
    };

    void verify();
  }, [reference]);

  useEffect(() => {
    if (!portalBaseUrl || status === "verifying") return;
    let portalOrigin: string;
    try {
      portalOrigin = new URL(portalBaseUrl).origin;
    } catch {
      return;
    }
    if (portalOrigin === window.location.origin) return;
    const next = new URL("/payment/callback", portalOrigin);
    next.search = window.location.search;
    window.location.replace(next.toString());
  }, [portalBaseUrl, status]);

  return {
    reference,
    status,
    invoiceId,
    invoiceType,
    portalBaseUrl,
    digitalResourceId,
    registration: invoiceType === "registration",
    thesis: invoiceType === "thesis",
    digitalResource: invoiceType === "digital_resource",
  };
}
